// Bank-statement upload card. After extraction, opens a ReviewModal where the
// user inspects/edits incomes + expenses before they are applied to the budget.
import React, { useCallback, useState } from "react";
import axios from "axios";
import { Upload, FileText, Loader2, X, Sparkles } from "lucide-react";
import { Card } from "./primitives";
import ReviewModal from "./ReviewModal";
import { API } from "../lib/budget";

const MODEL_OPTIONS = [
  { value: "claude-haiku-4-5", label: "Claude Haiku 4.5 (fastest)" },
  { value: "claude-sonnet-4-5", label: "Claude Sonnet 4.5 (smartest)" },
  { value: "gpt-5.2", label: "GPT-5.2" },
];

const UPLOAD_HEADERS = { "Content-Type": "multipart/form-data" };
const UPLOAD_TIMEOUT_MS = 180000;

const isAcceptedFile = (f) => {
  const n = (f.name || "").toLowerCase();
  return n.endsWith(".csv") || n.endsWith(".pdf");
};

const resolveUploadErrorMessage = (e) => {
  if (e?.response?.data?.detail) {
    return typeof e.response.data.detail === "string"
      ? e.response.data.detail
      : JSON.stringify(e.response.data.detail);
  }
  if (e?.code === "ECONNABORTED") {
    return "The request timed out. Try a smaller statement or Claude Haiku 4.5.";
  }
  if (e?.message === "Network Error") {
    return "Network error reaching the server. If you are on the live site, the file may be too large or the request timed out.";
  }
  return e?.message || "Upload failed.";
};

export default function UploadCard({ month, categories, currentIncome, onApply }) {
  const [files, setFiles] = useState([]);
  const [model, setModel] = useState("claude-haiku-4-5");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [error, setError] = useState("");

  const onFilesPicked = useCallback((list) => {
    const arr = Array.from(list || []).filter(isAcceptedFile);
    setFiles((prev) => [...prev, ...arr]);
    setError("");
  }, []);

  const removeFile = useCallback((idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleUpload = useCallback(async () => {
    if (files.length === 0) {
      setError("Please pick at least one CSV or PDF statement.");
      return;
    }
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("model", model);
      const r = await axios.post(`${API}/budget/${month}/upload`, fd, {
        headers: UPLOAD_HEADERS,
        timeout: UPLOAD_TIMEOUT_MS,
      });
      setResult(r.data);
      setReviewOpen(true);
      setFiles([]);
    } catch (e) {
      console.error(e);
      setError(resolveUploadErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }, [files, model, month]);

  const handleReviewApply = useCallback(async (decision) => {
    onApply(decision);
  }, [onApply]);

  return (
    <section className="mb-8">
      <Card className="p-6 sm:p-8" testId="card-upload" delay={0}>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded-full bg-[#E27D6022] text-[#B8593E]">
                AI POWERED
              </span>
              <h3 className="font-display text-xl font-semibold">Upload Bank Statements</h3>
            </div>
            <p className="text-sm text-[#5A5C58] max-w-xl">
              Drop your credit card and debit card statements (CSV or PDF). We&apos;ll separate income from expenses, auto-categorize each charge, and let you review everything before applying it to your budget.
            </p>

            <Dropzone onFilesPicked={onFilesPicked} />

            {files.length > 0 && <FileList files={files} onRemove={removeFile} />}

            {error && (
              <p data-testid="upload-error" className="mt-3 text-sm text-[#B8593E]">{error}</p>
            )}

            {result && !reviewOpen && (
              <button
                data-testid="reopen-review-btn"
                onClick={() => setReviewOpen(true)}
                className="mt-3 text-sm text-[#5F7161] hover:underline font-medium"
              >
                Re-open the last review →
              </button>
            )}
          </div>

          <UploadControls
            model={model}
            onModelChange={setModel}
            busy={busy}
            disabled={busy || files.length === 0}
            onSubmit={handleUpload}
          />
        </div>
      </Card>

      <ReviewModal
        open={reviewOpen}
        result={result}
        categories={categories}
        currentIncome={currentIncome}
        onClose={() => setReviewOpen(false)}
        onApply={handleReviewApply}
      />
    </section>
  );
}

// ------- Sub-components -------
const Dropzone = ({ onFilesPicked }) => (
  <label
    htmlFor="stmt-file-input"
    data-testid="upload-dropzone"
    className="mt-5 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#D8D6D0] hover:border-[#5F7161] hover:bg-[#F4F6F1]/60 rounded-2xl px-6 py-8 cursor-pointer transition-colors text-center"
    onDragOver={(e) => e.preventDefault()}
    onDrop={(e) => { e.preventDefault(); onFilesPicked(e.dataTransfer.files); }}
  >
    <div className="w-11 h-11 rounded-full bg-[#5F7161]/10 flex items-center justify-center text-[#5F7161]">
      <Upload size={20} />
    </div>
    <p className="text-sm font-medium text-[#1A1C1A]">
      Drag &amp; drop CSV / PDF statements, or click to browse
    </p>
    <p className="text-xs text-[#5A5C58]">Multiple files supported · Credit card &amp; debit card</p>
    <input
      id="stmt-file-input"
      data-testid="upload-file-input"
      type="file"
      accept=".csv,.pdf,text/csv,application/pdf"
      multiple
      className="hidden"
      onChange={(e) => onFilesPicked(e.target.files)}
    />
  </label>
);

const FileList = ({ files, onRemove }) => (
  <ul data-testid="upload-files-list" className="mt-4 space-y-2">
    {files.map((f, i) => (
      <li
        key={`${f.name}-${f.size}-${f.lastModified}`}
        className="flex items-center justify-between bg-[#F4F6F1] border border-[#E0E5DA] rounded-xl px-3 py-2 text-sm"
      >
        <span className="flex items-center gap-2 truncate">
          <FileText size={14} className="text-[#5F7161] shrink-0" />
          <span className="truncate" data-testid={`upload-file-${i}`}>{f.name}</span>
          <span className="text-[#8A8C88] font-mono text-xs shrink-0">
            {(f.size / 1024).toFixed(0)} KB
          </span>
        </span>
        <button
          data-testid={`upload-file-remove-${i}`}
          onClick={() => onRemove(i)}
          className="text-[#8A8C88] hover:text-[#B8593E] transition-colors p-1"
          aria-label="Remove file"
        >
          <X size={14} />
        </button>
      </li>
    ))}
  </ul>
);

const UploadControls = ({ model, onModelChange, busy, disabled, onSubmit }) => (
  <div className="md:w-72 shrink-0 space-y-3">
    <label className="text-sm font-medium block">Categorization Model</label>
    <select
      data-testid="upload-model-select"
      value={model}
      onChange={(e) => onModelChange(e.target.value)}
      className="w-full bg-white border border-[#D8D6D0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F7161]/40 focus:border-[#5F7161]"
    >
      {MODEL_OPTIONS.map((m) => (
        <option key={m.value} value={m.value}>{m.label}</option>
      ))}
    </select>
    <p className="text-xs text-[#5A5C58]">
      PDFs always use Claude Sonnet 4.5 for accurate column parsing.
    </p>
    <button
      data-testid="upload-submit-btn"
      onClick={onSubmit}
      disabled={disabled}
      className="w-full flex items-center justify-center gap-2 bg-[#5F7161] hover:bg-[#4E5C50] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
    >
      {busy ? (
        <><Loader2 size={14} className="animate-spin" /> Extracting…</>
      ) : (
        <><Sparkles size={14} /> Extract &amp; Review</>
      )}
    </button>
  </div>
);
